import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { DataHelpers } from '../../DataHelpers';
import { Customer } from '../../interfaces/Customer';
import { IsNeomyticConsultant } from '../../interfaces/Consultant';
import { customersActionCreators } from '../../store/customers';
import { dataActionCreators, DataState, Invoice } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { Accordion, AccordionHeader, AccordionItem, AccordionItemValue, AccordionPanel, AccordionToggleEventHandler, Button, Divider, InputOnChangeData, Popover, PopoverSurface, PopoverTrigger, SearchBox, SearchBoxChangeEvent, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Tag, Text } from '@fluentui/react-components';
import { GlobeDesktopIcon, GlobeArrowForwardIcon, DocumentOnePageAddIcon, EditIcon, FilterIcon, MailArrowForwardIcon, UploadIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { useState } from 'react';
import { TokenHelper } from '../../TokenHelper';
import { DownloadFileAndSave } from '../Tools/FileDownloadHelper';
import { useConsultants, useCustomers, useInvoices } from '../Tools/ObjectLoader';

interface InvoicesMonth {
    key: AccordionItemValue;
    docs: Invoice[];
    year: number;
    month: number;
    MonthTotalInvoice: number;
    MonthTotalPayed: number;
    MonthTotalInvoiceHTVA: number;
    needDisplay: boolean;
}

interface InvoicesTabProps {
    viewCreditNotes: boolean;
}

export const InvoicesTab = ({ viewCreditNotes }: InvoicesTabProps) => {
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const { invoices } = useInvoices();
    const { customers } = useCustomers();
    const { consultants, consultantsLoading } = useConsultants();
    const [filter, setFilter] = useState<string | null>(null);
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const [filterCustomer, setFilterCustomer] = useState<Customer | undefined>(undefined);
    const stylesTab = useTabStyles();
    const styles = useEditorStyles();
    const [invoicesFilter, setInvoicesFilter] = useState<InvoicesMonth[]>([]);
    const [defaultOpenItems, setdefaultOpenItems] = useState<AccordionItemValue[]>([]);

    // Check if all needed datas are retrieven
    React.useEffect(() => {
        // used in GetInvoiceTotalFromInvoice
        if (data.timeSheetEntries === undefined && data.timeSheetEntriesLoading === false) {
            dispatch(dataActionCreators.requestTimeSheetEntries(instance, teamsState));
        }
        if (data.projectsAssigments === undefined && data.projectsAssigmentsLoading === false) {
            dispatch(dataActionCreators.requestProjectsAssigments(instance, teamsState));
        }
        if (data.sales === undefined && data.salesLoading === false) {
            dispatch(dataActionCreators.requestSells(instance, teamsState));
        }
    }, [data]);

    React.useEffect(() => {
        if (invoices !== undefined) {
            let filteredInvoices = invoices;
            filteredInvoices = filteredInvoices.filter(i => IsNeomyticConsultant(i.consultantid));
            filteredInvoices = filteredInvoices.filter(i => i.isCreditNote === viewCreditNotes);

            // Apply filter if any
            if (filterCustomer) {
                filteredInvoices = filteredInvoices
                    .filter(i => {
                        if (i.customerid === filterCustomer.customerid) {
                            return true;
                        }
                        return false;
                    });
            }
            if (filter && filter.length != 0) {
                filteredInvoices = filteredInvoices
                    .filter(i => i.invoiceRef && i.invoiceRef.toLowerCase().includes(filter.toLowerCase()));
            }
            const filteredInvoicesMonth: InvoicesMonth[] = [];
            let LastYear = -1;
            let LastMonth = -1;
            let CurrentInvoicesMonth: InvoicesMonth;

            filteredInvoices
                .forEach((i) => {
                    const d = i.invoiceDate ? new Date(i.invoiceDate) : new Date();
                    if (d.getFullYear() !== LastYear || d.getMonth() != LastMonth) {
                        LastYear = d.getFullYear();
                        LastMonth = d.getMonth();
                        CurrentInvoicesMonth = {
                            key: "InvList" + (LastMonth + 1) + "/" + LastYear,
                            docs: [],
                            year: LastYear,
                            month: (LastMonth + 1),
                            MonthTotalInvoice: 0,
                            MonthTotalPayed: 0,
                            MonthTotalInvoiceHTVA: 0,
                            needDisplay: false,
                        };
                        filteredInvoicesMonth.push(CurrentInvoicesMonth);
                    }
                    CurrentInvoicesMonth.MonthTotalInvoiceHTVA += DataHelpers.GetInvoiceTotalFromInvoiceHTVA(data, i);
                    const cust = customers ? customers.find(e => e.customerid === i.customerid) : undefined;
                    const cons = consultants ? consultants.find(e => e.consultantid === i.consultantid) : undefined;
                    const InvoiceTotal = DataHelpers.GetInvoiceTotalFromInvoice(data, cons, cust, i);
                    CurrentInvoicesMonth.MonthTotalInvoice += InvoiceTotal;
                    CurrentInvoicesMonth.MonthTotalPayed += (i.payment ? i.payment : 0);
                    CurrentInvoicesMonth.docs.push(i);
                    if (i.payment.toFixed(2) != InvoiceTotal.toFixed(2)) {
                        CurrentInvoicesMonth.needDisplay = true;
                    }
                });
            setdefaultOpenItems(filteredInvoicesMonth.filter(i => i.needDisplay).map(i => i.key));
            setInvoicesFilter(filteredInvoicesMonth);
        }
    }, [invoices, filterCustomer, filter, viewCreditNotes]);

    const columns = [
        { columnKey: "Invoices", label: t('Invoices'), className: stylesTab.width17 },
        { columnKey: "Status", label: "Status", className: stylesTab.width17 },
        { columnKey: "Customers", label: t('Customers'), className: stylesTab.width17 },
        { columnKey: "Billed", label: t('Billed') + " (" + t('IncludVAT') + ")", className: stylesTab.width17 },
        { columnKey: "Paid", label: t('Paid') + " (" + t('IncludVAT') + ")", className: stylesTab.width17 },
    ];

    const mapInvoice = (i: Invoice): JSX.Element => {
        const cust = customers ? customers.find(e => e.customerid === i.customerid) : undefined;
        const cons = consultants ? consultants.find(e => e.consultantid === i.consultantid) : undefined;
        const InvoiceTotal = cust ? DataHelpers.GetInvoiceTotalFromInvoice(data, cons, cust, i) : 0;
        let InvoiceStatus = <Text className={stylesTab.InlineCenteredWarningText2}>{t('NotEmailed')}</Text>;
        if (i.invoiceSharePointFileUniqueId === null) {
            InvoiceStatus = <Text className={stylesTab.InlineCenteredWarningText2}>{t('NoInvoiceFile')}</Text>;
        }
        else if (i.payment > 0 || i.paymentDate !== null) {
            if (i.payment.toFixed(2) == InvoiceTotal.toFixed(2)) {
                InvoiceStatus = <Text className={stylesTab.InlineCenteredSuccessText}>{t('Paid')}</Text>;
            }
            else {
                InvoiceStatus = <Text className={stylesTab.InlineCenteredWarningText}>{t('Balance')}</Text>;
            }
        }
        else if (i.emailSent || i.peppolSent) {
            InvoiceStatus = <Text className={stylesTab.InlineCenteredDangerText}>{t('NotPaid')}</Text>;
        }
        return <TableRow key={i.invoiceid}>
            <TableCell>
                <Button appearance="transparent" className={stylesTab.TestButton} onClick={() => dispatch(dataActionCreators.requestInvoiceQuickView(i))} >
                    {i.invoiceRef ? i.invoiceRef : "?"}
                </Button>
            </TableCell>
            <TableCell>
                {InvoiceStatus}
            </TableCell>
            <TableCell>
                {cust ?
                    <Button appearance="transparent" className={stylesTab.TestButton} onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(cust))} >{cust.displayName ? cust.displayName : "?"}</Button>
                    :
                    <Text>?</Text>
                }
            </TableCell>
            <TableCell>
                <Text>{InvoiceTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            </TableCell>
            <TableCell>
                <TableCellLayout>
                    <Text>{(i.payment > 0 ? i.payment.toLocaleString(undefined, { style: 'currency', currency: 'EUR' }) : "- €")}</Text>
                </TableCellLayout>
                <TableCellActions>
                    <Button icon={<UploadIcon />} appearance="subtle" className={stylesTab.TestButton} title="Upload" onClick={() => dispatch(dataActionCreators.requestFileUploadInvoice(i))} />
                    <Button icon={<EditIcon />} appearance="subtle" className={stylesTab.TestButton} title={t('Edit')} onClick={() => dispatch(dataActionCreators.requestInvoiceEdition(i))} />
                    <Button icon={<DocumentOnePageAddIcon />} appearance="subtle" className={stylesTab.TestButton} title={t('GenInvoice')} onClick={() => {
                        dispatch(dataActionCreators.requestInvoiceGeneration(i));
                    }} />
                    {i.invoiceSharePointFileUniqueId &&
                        <Button icon={<MailArrowForwardIcon />} appearance="subtle" className={stylesTab.TestButton} title={t('EmailInvoice')} onClick={() => {
                            dispatch(dataActionCreators.requestInvoiceEmail(i));
                        }} />
                    }
                    {cust && cust.peppolIdentifier && cust.peppolIdentifier.length > 1 &&
                        <>
                            <Button icon={<GlobeDesktopIcon />} appearance="subtle" className={stylesTab.TestButton} title={t("DownloadPeppolInvoice")} onClick={() => {
                                TokenHelper.GetToken(instance, teamsState)
                                    .then(token => {
                                        if (token !== null && i.invoiceid) {
                                            DownloadFileAndSave('api/Invoices/' + i.invoiceid + "/GenUBLInvoice", token);
                                        }
                                    });
                            }} />
                            <Button icon={<GlobeArrowForwardIcon />} appearance="subtle" className={stylesTab.TestButton} title={t('PeppolInvoice')} onClick={() => {
                                dispatch(dataActionCreators.requestInvoiceSendUBL(i));
                            }} />
                        </>
                    }
                </TableCellActions>
            </TableCell>
        </TableRow>;
    };

    const handleToggle: AccordionToggleEventHandler<AccordionItemValue> = (event, data) => {
        setdefaultOpenItems(data.openItems);
    };
    return (
        <div className={stylesTab.rootSub}>
            <div className={stylesTab.MainBar}>
                <Popover positioning="below-start">
                    <PopoverTrigger>
                        <Button icon={<FilterIcon />} shape="circular" title="Filter" />
                    </PopoverTrigger>
                    <PopoverSurface>
                        <div className={stylesTab.List}>
                            {t('Customer')}
                            <CustomerDropdown
                                currentCustomer={filterCustomer}
                                customers={customers ?? []}
                                setCustomer={setFilterCustomer} />
                        </div>
                    </PopoverSurface>
                </Popover>
                {filterCustomer &&
                    <Tag
                        dismissible
                        dismissIcon={{ "aria-label": "remove" }}
                        size="small"
                        onClick={() => { setFilterCustomer(undefined); }}
                        appearance="outline"
                    >
                        {t('Customer') + " = " + filterCustomer.displayName}
                    </Tag>}
                <SearchBox
                    placeholder={t('Contains')}
                    onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                    dismiss={{ onClick: () => { setFilter(null); } }}
                    className={styles.fluid}
                />
                <div className={stylesTab.MarginLeftAuto}>
                    {/* <Button icon={<AddIcon />} shape="circular" title={t('Add')} onClick={() => {
                        const p: Invoice = {
                            invoiceid: null,
                            customerid: null,
                            invoiceRef: null,
                            invoiceDate: null,
                            paymentDate: null,
                            payment: 0,
                            invoiceSharePointFileUniqueId: null,
                            vatcode: 21,
                            description: null,
                            invoiceDescription: null,
                            buyerReference: null,
                            consultantid: NeomyticConsultantID, // neomytic consultant
                            pdfuploaded: false,
                            prepaidAmount: null,
                            lock: false,
                            isCreditNote: false,
                        };
                        dispatch(dataActionCreators.requestInvoiceEdition(p));
                    }} />
                    <Button icon={<ArrowClockwiseIcon />} shape="circular" title="Reload" onClick={() => {
                        dispatch(dataActionCreators.ReloadInvoicesEdition());
                    }} /> */}
                </div>
            </div>
            <Accordion multiple collapsible openItems={defaultOpenItems} onToggle={handleToggle}>
                {invoicesFilter.filter((cds) => cds.docs.length > 0).map((cds, index) => {
                    return <AccordionItem key={cds.year + + "/" + cds.month + "//" + index} value={cds.key} >
                        <AccordionHeader><Divider appearance="brand">{(cds.month) + "/" + cds.year}</Divider></AccordionHeader>
                        <AccordionPanel>
                            <Table arial-label="Default table">
                                <TableHeader>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableHeaderCell key={column.columnKey} className={column.className}>
                                                {column.label}
                                            </TableHeaderCell>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {cds.docs.map(p => mapInvoice(p))}
                                </TableBody>
                            </Table>
                            <div className={stylesTab.FlexCenterGap}>
                                <Text className={stylesTab.InlineCenteredSuccessText}>{t('Billed') + " " + cds.MonthTotalInvoiceHTVA.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                <Text className={stylesTab.InlineCenteredSuccessText}>{t('Billed') + " (" + t('IncludVAT') + ") " + cds.MonthTotalInvoice.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                <Text className={stylesTab.InlineCenteredSuccessText}>{t('Paid') + " (" + t('IncludVAT') + ") " + cds.MonthTotalPayed.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                {((cds.MonthTotalInvoice - cds.MonthTotalPayed > 0)
                                    ? <Text className={stylesTab.InlineCenteredDangerText}>{t('Due') + " (" + t('IncludVAT') + ") " + (cds.MonthTotalInvoice - cds.MonthTotalPayed).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                    : ((cds.MonthTotalInvoice - cds.MonthTotalPayed < 0)
                                        ? <Text className={stylesTab.InlineCenteredWarningText}>{(cds.MonthTotalInvoice - cds.MonthTotalPayed).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                                        : <Text className={stylesTab.InlineCenteredSuccessText}>{t('Balanced')}</Text>))
                                }
                            </div>
                        </AccordionPanel>
                    </AccordionItem>;
                })}
            </Accordion>
        </div>
    );
};